import reduce from 'lodash/reduce';
import without from 'lodash/without';
import omit from 'lodash/omit';
import map from 'lodash/map';
import reject from 'lodash/reject';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

import {
  SET_SITE_VIEWER_VERSION,
  SET_IS_IN_PAGE_SEARCH_ENABLED,
  SET_IS_GRID_TAXONOMY_ENABLED,
  ADD_GRID_TAXONOMY_CATEGORY,
  REMOVE_GRID_TAXONOMY_CATEGORY,
  SET_GRID_TAXONOMY_CATEGORY_DATA,
  SET_GRID_TAXONOMY_CATEGORIES_ORDER,
  SET_GRID_TAXONOMY_FILTER_TYPE,
  SET_GRID_TAXONOMY_FILTER_LOGICAL_OPERATOR,
  SET_GRID_TAXONOMY_FILTER_TOGGLE_TEXT,
  SET_HEADER_CONTENT_POSITIONING,
  SET_DISPLAY_ANCHOR_LINKS,
  SET_IS_FULL_BLEED,
  SET_LAYOUT_HEADER_STYLE,
  SET_IS_USING_LEGACY_HEADER,
  SET_IS_SECONDARY_LOGO_ENABLED,
  SET_SECONDARY_LOGO_DATA,
  SET_SHOULD_SHOW_HEADER,
  SET_SHOULD_SHOW_TITLE,
  SET_SHOULD_SHOW_SOCIAL_LINKS,
  SET_IS_INLINE_HEADER_STYLE,
  SET_TRANSLATIONS_IS_ENABLED,
  SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO,
  SET_TRANSLATIONS_SELECTED_LANGUAGE_ISOS,
  SET_TRANSLATIONS_DEFAULT_LANGUAGE_ISO,
  SET_TRANSLATIONS_APPLIED_LANGUAGES,
  MASK_PAGE_ENTITIES_STATE
} from '../action_types.js';

import {
  SECTION_TYPE_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';

import {
  getSubpageForSection,
  getSectionIndexInSubpage
} from 'zoomforth_codesbane_viewer/reducers/page_entities.selectors.js';


const makeStateForNewSectionOrdering = (state, subpage, oldSectionIndex, indexChange)=> {

  const orderedSectionIds = [...subpage.orderedSectionIds]; // make copy
  const sectionId = orderedSectionIds[`${oldSectionIndex}`];
  orderedSectionIds.splice(oldSectionIndex, 1);
  orderedSectionIds.splice(oldSectionIndex + indexChange, 0, sectionId);

  return {
    ...state,
    subpagesById: {
      ...state.subpagesById,
      [subpage.id]: {
        ...subpage,
        orderedSectionIds: orderedSectionIds
      }
    }
  };

};


export default (state = {}, action)=> {

  switch (action.type) {

  case MASK_PAGE_ENTITIES_STATE: {
    const { newPageEntities } = action;
    const updatedState = cloneDeep(state);

    merge(updatedState, newPageEntities);

    return updatedState;
  }

  case 'ADD_PAGE_CONTENT_ITEM': {
    return {
      ...state,
      pageContentItemsByUuid: {
        ...state.pageContentItemsByUuid,
        [action.contentItem.uuid]: action.contentItem
      }
    };
  }


  case 'ADD_SECTION': {
    // Add _isInsideTab flag if section is being added to a tab
    const sectionWithTabFlag = action.tab ? {
      ...action.section,
      _isInsideTab: true
    } : action.section;

    const newState = {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.section.id]: sectionWithTabFlag
      }
    };

    // If tab is provided, add section to tab instead of subpage
    if (action.tab) {
      const parentSection = state.sectionsById[action.tab.parentSectionId];
      const updatedTabs = parentSection.tabs.map(tab => {
        if (tab.id === action.tab.id) {
          // Only allow one section per tab - replace existing section if any
          return {
            ...tab,
            content: {
              ...tab.content,
              sectionId: action.section.id
            }
          };
        }
        return tab;
      });

      newState.sectionsById = {
        ...newState.sectionsById,
        [parentSection.id]: {
          ...parentSection,
          tabs: updatedTabs
        }
      };
    } else {
      // Original behavior: add section to subpage
      const subpage = state.subpagesById[action.subpage.id];
      const orderedSectionIds = [...subpage.orderedSectionIds];
      orderedSectionIds.splice(action.index, 0, action.section.id);

      newState.subpagesById = {
        ...state.subpagesById,
        [subpage.id]: {
          ...subpage,
          orderedSectionIds: orderedSectionIds
        }
      };
    }

    return newState;
  }

  case 'CLEAR_TAB_CONTENT': {
    const parentSection = state.sectionsById[action.parentSectionId];
    const tabToClear = parentSection.tabs.find(tab => tab.id === action.tabId);
    
    const sectionIdToRemove = tabToClear?.content?.sectionId;
    
    const updatedTabs = parentSection.tabs.map(tab => {
      if (tab.id === action.tabId) {
        return {
          ...tab,
          content: {
            sectionId: null
          }
        };
      }
      return tab;
    });

    const newSectionsById = { ...state.sectionsById };
    if (sectionIdToRemove && newSectionsById[sectionIdToRemove]) {
      delete newSectionsById[sectionIdToRemove];
    }

    return {
      ...state,
      sectionsById: {
        ...newSectionsById,
        [parentSection.id]: {
          ...parentSection,
          tabs: updatedTabs
        }
      }
    };
  }

  case 'SET_EDITING_TAB_ID': {
    return {
      ...state,
      editingTabId: action.tabId,
      editingTabParentSectionId: action.parentSectionId
    };
  }

  case 'CLEAR_EDITING_TAB_ID': {
    return {
      ...state,
      editingTabId: null,
      editingTabParentSectionId: null
    };
  }


  case 'MOVE_SECTION_UP': {
    const sectionIndex = getSectionIndexInSubpage(state, action.section);
    const subpage = getSubpageForSection(state, action.section);
    return makeStateForNewSectionOrdering(state, subpage, sectionIndex, -1);
  }

  case 'MOVE_SECTION_DOWN': {
    const sectionIndex = getSectionIndexInSubpage(state, action.section);
    const subpage = getSubpageForSection(state, action.section);
    return makeStateForNewSectionOrdering(state, subpage, sectionIndex, 1);
  }
  
  case 'CREATE_MENU_ITEM':
    return {
      ...state,
      menuItemsById: {
        ...state.menuItemsById,
        [action.id]: {
          ...action.menuItem,
          id: action.id
        }
      },
      orderedMenuItemIds: action.menuItem.isSubmenuItem ? 
        state.orderedMenuItemIds // Don't add submenu items to main menu
      : 
        [ ...state.orderedMenuItemIds, action.id ]
    };  

  case 'DELETE_SUBPAGE': 

    return {
      ...state,
      subpagesById: {
        ...omit(state.subpagesById, [action.subpageId])
      }
    };

  case 'DELETE_MENU_ITEMS':
    return {
      ...state,
      menuItemsById: {
        ...omit(state.menuItemsById, action.menuItemIds)
      },
      orderedMenuItemIds: without.apply(null, [state.orderedMenuItemIds, ...action.menuItemIds])
    };

  case 'SET_COLUMN_COUNT': {
    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          columnCount: action.columnCount
        }
      }
    };
  }

  case 'SET_MENU_ITEM': {
    return {
      ...state,
      menuItemsById: {
        ...state.menuItemsById,
        [action.menuItem.id]: {
          ...action.menuItem
        }
      }
    };
  }

  case 'SET_MENU_ITEMS_ORDER': {
    return {
      ...state,
      orderedMenuItemIds: map(action.menuItems, 'id')
    };
  }

  case 'SET_PAGE_NAME': {
    return {
      ...state,
      title: action.pageName
    };
  }

  case 'SET_SECTION_AND_OVERRIDES': {
    const existingSection = state.sectionsById[action.sectionId];
    const isInsideTab = existingSection && existingSection._isInsideTab;
    
    const newState = {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          ...action.section,
          ...(isInsideTab && { _isInsideTab: true })
        }
      }
    };

    // Update any tabs that reference this section
    const updatedSectionsById = { ...newState.sectionsById };
    Object.keys(updatedSectionsById).forEach(sectionId => {
      const section = updatedSectionsById[sectionId];
      if (section.type === SECTION_TYPE_TABS && section.tabs) {
        const updatedTabs = section.tabs.map(tab => {
          if (tab.content && tab.content.section && tab.content.section.id === action.sectionId) {
            return {
              ...tab,
              content: {
                ...tab.content,
                section: {
                  ...tab.content.section,
                  ...action.section,
                  ...(isInsideTab && { _isInsideTab: true })
                }
              }
            };
          }
          return tab;
        });
        
        // Only update if tabs were actually modified
        if (updatedTabs.some((tab, index) => tab !== section.tabs[index])) {
          updatedSectionsById[sectionId] = {
            ...section,
            tabs: updatedTabs
          };
        }
      }
    });

    return {
      ...newState,
      sectionsById: updatedSectionsById
    };
  }

  case 'SET_SECTION_SIMPLE_DATA': {
    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          ...action.data
        }
      }
    };
  }

  case SET_IS_GRID_TAXONOMY_ENABLED: {
    var gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            isEnabled: action.isEnabled
          }
        }
      }
    };
  }

  case SET_GRID_TAXONOMY_FILTER_TYPE: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            filterMeta: {
              ...gridTaxonomy.filterMeta,
              type: action.filterType
            }
          }
        }
      }
    };
  }

  case SET_GRID_TAXONOMY_FILTER_LOGICAL_OPERATOR: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            filterMeta: {
              ...gridTaxonomy.filterMeta,
              logicalOperator: action.logicalOperator
            }
          }
        }
      }
    };
  }

  case SET_GRID_TAXONOMY_FILTER_TOGGLE_TEXT: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            filterMeta: {
              ...gridTaxonomy.filterMeta,
              toggleText: action.toggleText
            }
          }
        }
      }
    };
  }

  case ADD_GRID_TAXONOMY_CATEGORY: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            categories: [
              ...gridTaxonomy.categories,
              action.newCategory
            ]
          }
        }
      }
    };
  }

  case REMOVE_GRID_TAXONOMY_CATEGORY: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            categories: reject(gridTaxonomy.categories, ['id', action.categoryId])
          }
        }
      }
    };
  }

  case SET_GRID_TAXONOMY_CATEGORIES_ORDER: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    const orderedCategories = [...gridTaxonomy.categories]; // make copy
    const category = orderedCategories[action.oldCategoryIdx];

    orderedCategories.splice(action.oldCategoryIdx, 1);
    orderedCategories.splice(action.newCategoryIdx, 0, category);

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            categories: orderedCategories
          }
        }
      }
    };
  }

  case SET_GRID_TAXONOMY_CATEGORY_DATA: {
    const gridTaxonomy = state.sectionsById[action.sectionId]['gridTaxonomy'];

    return {
      ...state,
      sectionsById: {
        ...state.sectionsById,
        [action.sectionId]: {
          ...state.sectionsById[action.sectionId],
          gridTaxonomy: {
            ...gridTaxonomy,
            categories: map(gridTaxonomy.categories, (category)=> {
              if (category.id === action.categoryId) {
                return { ...category, ...action.data };
              }
              return category;
            })
          }
        }
      }
    };
  }

  case 'SET_SUBPAGE_DATA': {
    return {
      ...state,
      subpagesById: {
        ...state.subpagesById,
        [action.subpageId]: {
          ...state.subpagesById[action.subpageId],
          ...action.data
        }
      }
    };
  }

  case 'SET_CONTENT_ITEM_ASSET': {
    return {
      ...state,
      pageContentItemsByUuid: {
        ...state.pageContentItemsByUuid,
        [action.contentUuid]: {
          ...state.pageContentItemsByUuid[action.contentUuid],
          asset_id: action.asset.assetId,
          asset_type: action.asset.assetType,
          tile_inner_zf_image: action.tile_inner_zf_image
        }
      },
      assetsByIdent: {
        ...state.assetsByIdent,
        [`${action.asset.assetType}-${action.asset.assetId}`]: action.asset
      }
    };
  }

  case 'SET_CONTENT_ITEM_SIMPLE_DATA': {
    return {
      ...state,
      pageContentItemsByUuid: {
        ...state.pageContentItemsByUuid,
        [action.contentUuid]: {
          ...state.pageContentItemsByUuid[action.contentUuid],
          ...action.data
        }
      }
    };
  }
  
  case 'UPDATE_ASSET_DATA': {
    return {
      ...state,
      assetsByIdent: {
        ...state.assetsByIdent,
        [`${action.asset.assetType}-${action.asset.assetId}`]: action.asset
      }
    };
  }

  case 'SET_CURRENT_THEME':
    return {
      ...state,
      themeUuid: action.uuid
    };

  case 'SET_PAGE_SCOPED_CSS':
    return {
      ...state,
      pageScopedCss: action.pageScopedCss
    };

  case 'DELETE_CONTENT_ITEMS': {

    return {
      ...state,
      pageContentItemsByUuid: omit(state.pageContentItemsByUuid, action.contentUuids)
    };
  }

  case 'REMOVE_SECTIONS': {
    const sectionIds = action.sectionIds;

    return {
      ...state,
      sectionsById: omit(state.sectionsById, sectionIds),
      subpagesById: reduce(state.subpagesById, (accu, { orderedSectionIds, ...rest }, subpageId)=> ({
        ...accu,
        [subpageId]: {
          ...rest,
          orderedSectionIds: without.apply(null, [orderedSectionIds, ...sectionIds])
        }

      }), {})
    };
  }

  case 'SET_SOCIAL_LINKS': {
    return {
      ...state,
      socialLinks: action.socialLinks
    };
  }

  case 'SET_IS_MOBILE_RENDER_ENABLED': {
    return {
      ...state,
      isMobileRenderEnabled: action.value
    };
  }

  case SET_IS_IN_PAGE_SEARCH_ENABLED: {
    return {
      ...state,
      isInPageSearchEnabled: action.value
    };
  }
      
  case SET_SITE_VIEWER_VERSION: {
    return {
      ...state,
      siteViewerVersion: action.siteViewerVersion
    };
  }

  case SET_IS_SECONDARY_LOGO_ENABLED: {
    return {
      ...state,
      isSecondaryLogoEnabled: action.value
    };
  }

  case SET_HEADER_CONTENT_POSITIONING: {
    return {
      ...state,
      headerContentPosition: action.value
    };
  }

  case SET_SECONDARY_LOGO_DATA: {
    return {
      ...state,
      secondaryLogoData: action.data
    };
  }

  case SET_SHOULD_SHOW_HEADER: {
    return {
      ...state,
      shouldShowHeader: action.value
    };
  }

  case SET_SHOULD_SHOW_TITLE: {
    return {
      ...state,
      shouldShowTitle: action.value
    };
  }

  case SET_SHOULD_SHOW_SOCIAL_LINKS: {
    return {
      ...state,
      shouldShowSocialLinks: action.value
    };
  }
  
  
  case SET_IS_INLINE_HEADER_STYLE: {
    return {
      ...state,
      isInlineHeaderStyle: action.value
    };
  }
  
  case SET_DISPLAY_ANCHOR_LINKS: {
    return {
      ...state,
      displayAnchorLinks: action.value
    };
  }

  case SET_IS_FULL_BLEED: {
    return {
      ...state,
      isFullBleed: action.value
    };
  }

  case SET_LAYOUT_HEADER_STYLE: {
    return {
      ...state,
      headerContentPosition: action.value
    };
  }

  case SET_IS_USING_LEGACY_HEADER: {
    return {
      ...state,
      isUsingLegacyHeader: action.value
    };
  }

  case SET_TRANSLATIONS_IS_ENABLED: {
    return {
      ...state,
      translationsIsEnabled: action.value
    };
  }

  case SET_TRANSLATIONS_CURRENT_LANGUAGE_ISO: {
    return {
      ...state,
      translationsCurrentLanguageIso: action.value
    };
  }

  case SET_TRANSLATIONS_SELECTED_LANGUAGE_ISOS: {
    return {
      ...state,
      translationsSelectedLanguageIsos: action.value
    };
  }

  case SET_TRANSLATIONS_DEFAULT_LANGUAGE_ISO: {
    return {
      ...state,
      translationsDefaultLanguageIso: action.value,
      translationsCurrentLanguageIso: action.value
    };
  }

  case SET_TRANSLATIONS_APPLIED_LANGUAGES: {
    return {
      ...state,
      translationsAppliedLanguages: action.value
    };
  }

  default: {
    return state;
  }

  }

};


//// See `zoomforth_codesbane_viewer/reducers/page_entities.selectors.js` for the page selectors
